import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Drawer, Box, Typography, TextField, IconButton, Avatar, Button,
  Chip, Divider, InputAdornment, CircularProgress,
  Menu, MenuItem, List, ListItem, ListItemButton,
} from '@mui/material';
import { Close, Search, Person, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateOrderMutation } from '../../services/orderApi';
import { setOrderSalesperson, setItemSalesperson, setCurrentOrder } from '../../store/orderSlice';
import { useToast } from '../common/ToastProvider';

// onSelect / currentSalesperson: when given (exchange mode), a pick goes to
// the caller instead of the normal bill — no target menu, no order update.
const SalespersonDrawer = ({ open, onClose, salespersons = [], onSelect, currentSalesperson }) => {
  const dispatch = useDispatch();
  const showToast = useToast();
  const { currentOrderId, cartItems, selectedSalesperson: orderSalesperson } = useSelector((state) => state.order);
  const selectedSalesperson = onSelect ? currentSalesperson : orderSalesperson;

  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingSP, setPendingSP] = useState(null);
  const [focusIndex, setFocusIndex] = useState(-1);
  const itemRefs = useRef([]);

  const filteredSPs = useMemo(() => {
    if (!search.trim()) return salespersons;
    const term = search.toLowerCase();
    return salespersons.filter(
      (sp) => sp.name.toLowerCase().includes(term) || (sp.phone_number || '').includes(term)
    );
  }, [search, salespersons]);

  const handleClose = () => {
    setSearch('');
    setPendingSP(null);
    setAnchorEl(null);
    setFocusIndex(-1);
    onClose();
  };

  const openTargetMenu = (e, sp) => {
    setPendingSP(sp);
    setAnchorEl(e.currentTarget);
  };

  // Assign / Enter: exchange mode picks directly; otherwise ask order vs item.
  const choose = (e, sp) => {
    if (onSelect) {
      onSelect({ id: sp.id, name: sp.name });
      showToast(`${sp.name} assigned!`, 'success');
      handleClose();
      return;
    }
    openTargetMenu(e, sp);
  };
  const chooseRef = useRef(choose);
  useEffect(() => { chooseRef.current = choose; });

  const closeTargetMenu = () => {
    setAnchorEl(null);
    setPendingSP(null);
  };

  // Roving focus over the salesperson list: ArrowUp/Down move, Enter opens the "Assign to" menu
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (filteredSPs.length === 0 || anchorEl) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex(i => (i + 1 >= filteredSPs.length ? 0 : i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex(i => (i <= 0 ? filteredSPs.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter' && focusIndex >= 0 && filteredSPs[focusIndex]) {
        e.preventDefault();
        const node = itemRefs.current[focusIndex];
        if (node) chooseRef.current({ currentTarget: node }, filteredSPs[focusIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, filteredSPs, focusIndex, anchorEl]);

  const handleAssign = async (target) => {
    setAnchorEl(null);
    if (!pendingSP) return;
    const sp = pendingSP;
    setPendingSP(null);

    try {
      if (target === 'order') {
        dispatch(setOrderSalesperson({ id: sp.id, name: sp.name }));
        if (currentOrderId) {
          const res = await updateOrder({ id: currentOrderId, salesperson_id: sp.id }).unwrap();
          dispatch(setCurrentOrder(res.data.order));
        }
      } else {
        // target = order_item_id
        dispatch(setItemSalesperson({ order_item_id: target, salesperson_id: sp.id, salesperson_name: sp.name }));
        if (currentOrderId) {
          const res = await updateOrder({
            id: currentOrderId,
            order_items_attributes: [{ id: target, salesperson_id: sp.id }],
          }).unwrap();
          dispatch(setCurrentOrder(res.data.order));
        }
      }
      showToast(`${sp.name} assigned!`, 'success');
    } catch (err) {
      showToast(err.data?.error || 'Failed to assign salesperson', 'error');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
      }}>
        <Person sx={{ color: '#10B981', fontSize: 22 }} />
        <Typography fontWeight={800} fontSize="1rem" color="#1E293B" sx={{ flexGrow: 1 }}>
          Assign Salesperson
        </Typography>
        {selectedSalesperson && (
          <Chip
            size="small"
            label={selectedSalesperson.name}
            sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: '#F0FDF4', color: '#16A34A', height: 22 }}
          />
        )}
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
      }}>

        {/* Search */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setFocusIndex(-1); }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#94A3B8', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: isLoading ? <CircularProgress size={16} /> : null,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.9rem' } }}
          />
        </Box>

        {/* SP list label */}
        <Box sx={{ px: 2.5, mb: 1 }}>
          <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.06rem' }}>
            SALESPERSONS
          </Typography>
        </Box>

        {/* SP list */}
        <Box sx={{ px: 2.5 }}>
          {salespersons.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Person sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
              <Typography fontSize="0.85rem" fontWeight={600} color="#64748B">
                No salespersons available
              </Typography>
              <Typography fontSize="0.75rem" color="#94A3B8">
                No salespersons are assigned to this store
              </Typography>
            </Box>
          ) : filteredSPs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography fontSize="0.82rem" color="#94A3B8">
                No match for "{search}"
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
              {filteredSPs.map((sp, i) => {
                const isFocused = i === focusIndex;
                return (
                  <React.Fragment key={sp.id}>
                    {i > 0 && <Divider />}
                    <ListItem
                      disablePadding
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => choose(e, sp)}
                          sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                            borderRadius: '6px', borderColor: '#10B981', color: '#10B981',
                            py: 0.4, px: 1.2, mr: 1,
                            '&:hover': { bgcolor: '#F0FDF4', borderColor: '#10B981' },
                          }}
                        >
                          Assign
                        </Button>
                      }
                    >
                      <ListItemButton
                        ref={(el) => { itemRefs.current[i] = el; }}
                        sx={{
                          py: 1.25, pl: 2, pr: 10, gap: 1.5, '&:hover': { bgcolor: '#F8FAFC' },
                          bgcolor: isFocused ? '#EEF2FF' : 'transparent',
                          borderLeft: isFocused ? '3px solid #4F46E5' : '3px solid transparent',
                        }}
                      >
                        <Avatar sx={{
                          bgcolor: '#F0FDF4', color: '#10B981', fontWeight: 800,
                          fontSize: '0.85rem', width: 36, height: 36, flexShrink: 0,
                        }}>
                          {sp.name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={700} fontSize="0.88rem" color={isFocused ? '#4F46E5' : '#1E293B'} noWrap>
                            {sp.name}
                          </Typography>
                          {sp.phone_number && (
                            <Typography fontSize="0.72rem" color="#94A3B8">{sp.phone_number}</Typography>
                          )}
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* "Assign to" target menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeTargetMenu}
          PaperProps={{ sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 220 } }}
        >
          <MenuItem disabled sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem', opacity: '1 !important', py: 0.75 }}>
            ASSIGN {pendingSP?.name?.toUpperCase()} TO
          </MenuItem>

          {/* Order-level target */}
          <MenuItem onClick={() => handleAssign('order')} sx={{ gap: 1.5, py: 1.25 }}>
            <CheckCircle sx={{
              fontSize: 16,
              color: selectedSalesperson?.id === pendingSP?.id ? '#10B981' : '#E2E8F0',
            }} />
            <Box>
              <Typography fontWeight={700} fontSize="0.88rem">Entire Order</Typography>
              {selectedSalesperson && (
                <Typography fontSize="0.7rem" color="#94A3B8">
                  Currently: {selectedSalesperson.name}
                </Typography>
              )}
            </Box>
          </MenuItem>

          {cartItems.length > 0 && <Divider sx={{ my: 0.5 }} />}

          {/* Per-item targets */}
          {cartItems.map((item) => (
            <MenuItem
              key={item.order_item_id}
              onClick={() => handleAssign(item.order_item_id)}
              sx={{ gap: 1.5, py: 1.25 }}
            >
              <CheckCircle sx={{
                fontSize: 16,
                color: item.salesperson_id === pendingSP?.id ? '#10B981' : '#E2E8F0',
              }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} fontSize="0.85rem" noWrap sx={{ maxWidth: 170 }}>
                  {item.product_name}
                </Typography>
                {item.salesperson_name && (
                  <Typography fontSize="0.7rem" color="#94A3B8">
                    Currently: {item.salesperson_name}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
        <Button
          fullWidth variant="contained" disableElevation onClick={handleClose}
          sx={{
            fontWeight: 800, textTransform: 'none', borderRadius: '8px',
            bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' }, height: 44,
          }}
        >
          Done
        </Button>
      </Box>
    </Drawer>
  );
};

export default SalespersonDrawer;
